import { useEffect, useRef, useCallback } from 'react';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../features/user/userSlice';
import UserPool from '../UserPool';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Custom hook to automatically refresh AWS Cognito tokens and sign out after inactivity.
 * - Refreshes token every `refreshIntervalMinutes` while the user is active.
 * - Signs out automatically after inactivity, duration driven by the
 *   `session_timeout_minutes` field in the ui_defaults system config (default: 15 min).
 *
 * @param {number} refreshIntervalMinutes - Interval in minutes to refresh tokens (default: 10)
 */
const useTokenRefresh = (refreshIntervalMinutes = 10) => {
  const dispatch = useDispatch();
  const tokenIntervalRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  // Read session timeout from system config (ui_defaults → SessionTimoutMinutes → timeout[0].minutes)
  // Enforces a minimum of 5 minutes and defaults to 15 if not configured
  const rawSessionTimeout = useSelector(
    (state) => state.reference?.SessionTimoutMinutes?.timeout?.[0]?.minutes
  );
  const sessionTimeoutMinutes = rawSessionTimeout ? parseInt(rawSessionTimeout, 10) : null;
  const inactivityTimeoutMs = Math.max(5, sessionTimeoutMinutes ?? 5) * 60 * 1000;

  // Full sign-out: Amplify + Cognito User Pool + Redux + localStorage
  const performLogout = useCallback(async () => {
    // Session timed out — perform logout

    try {
      await amplifySignOut({ global: true });
    } catch (e) {
      // Amplify sign out failed silently
    }

    try {
      const cognitoUser = UserPool.getCurrentUser();
      if (cognitoUser) cognitoUser.signOut();
    } catch (e) {
      // Cognito sign out failed silently
    }

    localStorage.removeItem('user');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('reference');
    localStorage.removeItem('clientDetails');

    dispatch(logout());
    window.location.replace('/login');
  }, [dispatch, sessionTimeoutMinutes]);

  // Reset the inactivity countdown on every user activity event
  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(performLogout, inactivityTimeoutMs);
  }, [performLogout, inactivityTimeoutMs]);

  // Periodic token refresh
  useEffect(() => {
    const refreshToken = async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        if (session.tokens?.idToken) {
          // Token refreshed successfully
        } else {
          // No tokens available during refresh
        }
      } catch (error) {
        if (error.message?.includes('refresh') || error.message?.includes('expired')) {
          dispatch(logout());
          localStorage.clear();
          window.location.replace('/login');
        }
      }
    };

    // Refresh immediately on mount, then on the interval
    refreshToken();
    const intervalMs = refreshIntervalMinutes * 60 * 1000;
    tokenIntervalRef.current = setInterval(refreshToken, intervalMs);
    // Token refresh scheduled

    return () => clearInterval(tokenIntervalRef.current);
  }, [dispatch, refreshIntervalMinutes]);

  // Inactivity logout: start countdown on mount, reset it on any user activity.
  // Re-runs whenever sessionTimeoutMinutes changes (e.g. admin updates system config).
  useEffect(() => {
    const handleActivity = () => resetInactivityTimer();

    // Start countdown immediately when authenticated
    resetInactivityTimer();

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    return () => {
      clearTimeout(inactivityTimerRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [resetInactivityTimer]);
};

export default useTokenRefresh;
